<template>
  <div>
    <!-- Floating Heera notification modal -->
    <transition name="fade-slide">
      <div v-if="isVisible" class="heera-card-container pointer" @click="handleAction(currentContent.action)">
        <!-- Close Button (Inline SVG X Icon) -->
        <button class="close-card-btn" @click.stop="closeCard" aria-label="Close notification">
          <img
            class="close-icon-img"
            src="@/assets/imgs/newhomepage/arrow-blue-.svg"
            alt="Close"
          />
        </button>

        <!-- Card Contents -->
        <div class="d-flex heera-card-content mt-1">
          <!-- Left side: Circular Avatar -->
          <div class="avatar-column">
            <img
              src="@/assets/imgs/newhomepage/heera-icon.webp"
              alt="Heera Avatar"
              class="heera-avatar"
            />
          </div>

          <!-- Right side: Text and CTA button with transition -->
          <transition name="fade-content" mode="out-in">
            <div :key="currentContent.title" class="text-column mt-2">
              <h3 class="heera-title font-inter fs-20 fw-600 color-darkblack">
                {{ currentContent.title }}
              </h3>
              <p class="heera-desc font-inter fs-14 fw-400 lh-21 color-4D4D4D mb-0">
                {{ currentContent.desc }}
              </p>
              <button
                v-if="currentContent.action === 'scanResume'"
                class="heera-cta-btn font-inter fs-16 fw-500 color-white"
                @click="handleAction(currentContent.action)"
              >
                {{ currentContent.btnText }}
              </button>
            </div>
          </transition>
        </div>
      </div>
    </transition>

    <!-- Fallback Local Resume Clinic Modal (rendered if no global handler is present on the page) -->
    <ResumeClinic
      v-if="showClinic"
      :showModal="showClinic"
      :id="'resumeclinicmodal'"
      @modalClose="showClinic = false"
    />
  </div>
</template>

<script>
import EventBus from "@/utils/eventBus";
import ResumeClinic from "@/components/modalComponent/ResumeClinic.vue";
import config from "@/utils/config";
import { openLoginModal } from "@/utils/helpers";
import { COOKIE_OPTIONS_SUFFIX } from "@/utils/cookieConfig";

export default {
  name: "HeeraNotification",
  components: {
    ResumeClinic,
  },
  data() {
    return {
      isVisible: false,
      temporarilyHidden: false,
      showClinic: false,
      activeSection: null,
      intersectingSections: {},
      observer: null,
      sectionsConfig: [
        {
          selector: "#home-container",
          title: "Scan",
          desc: "Hi, I'm Heera 👋 Let's score your resume!",
          btnText: "Scan My Resume",
          action: "scanResume",
        },
        {
          selector: ".opputunity-container",
          title: "Create Profile",
          desc: "Hi, I'm Heera 👋 Let's build your profile!",
          btnText: "Create Profile",
          action: "createProfile",
        },
        {
          selector: "#courses-sec",
          title: "Courses",
          desc: "Hi, I'm Heera 👋 Ready to level up?",
          btnText: "Explore Courses",
          action: "exploreCourses",
        },
        {
          selector: "#assessments-sec",
          title: "Assessments",
          desc: "Hi, I'm Heera 👋 Test your skills!",
          btnText: "Take Assessment",
          action: "takeAssessment",
        },
        {
          selector: "#mock-interviews-sec",
          title: "Mock Interview",
          desc: "Hi, I'm Heera 👋 Let's practice!",
          btnText: "Start Mock Interview",
          action: "practiceInterviews",
        },
        {
          selector: "#jobs-sec",
          title: "Jobs",
          desc: "Hi, I'm Heera 👋 Your next job awaits!",
          btnText: "Explore Jobs",
          action: "exploreJobs",
        },
      ],
    };
  },
  computed: {
    currentContent() {
      if (this.activeSection) {
        return this.activeSection;
      }
      return {
        title: "Scan",
        desc: "Hi, I'm Heera 👋 Let's score your resume!",
        btnText: "Scan My Resume",
        action: "scanResume",
      };
    },
  },
  watch: {
    $route: {
      handler() {
        this.$nextTick(() => {
          this.initObserver();
        });
      },
      immediate: true,
    },
    isVisible(val) {
      if (val) {
        EventBus.$emit("heera-notification-opened");
      }
    },
  },
  created() {
    EventBus.$on("lazy-loading-complete", this.initObserver);
    EventBus.$on("user-dropdown-toggled", this.handleDropdownToggle);
  },
  mounted() {
    // Show the card after a short, premium delay unless it was closed in the current session
    if (!sessionStorage.getItem("hire3x_heera_dismissed")) {
      setTimeout(() => {
        this.isVisible = true;
      }, 2000);
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    EventBus.$off("lazy-loading-complete", this.initObserver);
    EventBus.$off("user-dropdown-toggled", this.handleDropdownToggle);
  },
  methods: {
    handleDropdownToggle(isOpen) {
      if (isOpen) {
        if (this.isVisible) {
          this.isVisible = false;
          this.temporarilyHidden = true;
        }
      } else {
        if (this.temporarilyHidden) {
          this.isVisible = true;
          this.temporarilyHidden = false;
        }
      }
    },
    closeCard() {
      this.isVisible = false;
      sessionStorage.setItem("hire3x_heera_dismissed", "true");
    },
    scanResume() {
      // Check if there are active global handlers on the EventBus for opening the clinic
      if (
        EventBus._events["resume-clinic-opened"] &&
        EventBus._events["resume-clinic-opened"].length > 0
      ) {
        EventBus.$emit("resume-clinic-opened");
      } else {
        // Fallback: open the modal locally within this component
        this.showClinic = true;
      }
    },
    handleAction(action) {
       const loggedIn = this.isLoggedIn || 
                         (this.$store && this.$store.getters && this.$store.getters["userVUEX/getCandAccess"]);
      if (action === "scanResume") {
        this.scanResume();
      } else if (action === "createProfile") {
        const redirectUrl = `${config.candidateBase}/dashboard?from=candidate`;
        if (loggedIn) {
          window.location.href = redirectUrl;
        } else {
          openLoginModal({ mode: "register", redirectUrl });
        }
      } else if (action === "exploreCourses") {
        window.location.href = `${config.trainingBase}/marketplace`;
      } else if (action === "takeAssessment") {
        window.location.href = `${config.candidateBase}/assessment/marketplace?from=candidate`;
      } else if (action === "practiceInterviews") {
        if(loggedIn) window.location.href = `${config.candidateBase}/interview?from=candidate`
        else{
        openLoginModal({
          redirectUrl: `${config.candidateBase}/interview?from=candidate`,
        });
      }
      } else if (action === "exploreJobs") {
        window.location.href = `${config.candidateBase}/job/search?from=candidate`;
      }
    },
    initObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.activeSection = null;
      this.intersectingSections = {};

      // Do not run observer on mobile since the card is hidden
      const isMobile = typeof window !== "undefined" && window.innerWidth <= 1028;
      if (isMobile) return;

      if (!window.IntersectionObserver) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const selector = entry.target.dataset.heeraSelector;
            if (selector) {
              this.$set(this.intersectingSections, selector, entry.isIntersecting);
            }
          });
          this.determineActiveSection();
        },
        {
          rootMargin: "-10% 0px -30% 0px",
          threshold: 0.1,
        }
      );

      this.sectionsConfig.forEach((cfg) => {
        const elements = document.querySelectorAll(cfg.selector);
        elements.forEach((el) => {
          el.dataset.heeraSelector = cfg.selector;
          this.observer.observe(el);
        });
      });
    },
    determineActiveSection() {
      const activeCfg = this.sectionsConfig.find(
        (cfg) => this.intersectingSections[cfg.selector]
      );
      if (activeCfg) {
        this.activeSection = activeCfg;
        this.markSectionAsVisited(activeCfg.action);
      }
    },
    markSectionAsVisited(action) {
      let active = ["scanResume"];
      try {
        let cookieValue = null;
        if (typeof document !== "undefined" && document.cookie) {
          const match = document.cookie.match(/(^|;)\s*hire3x_heera_notifications\s*=\s*([^;]+)/);
          if (match) cookieValue = match[2];
        }
        if (cookieValue) {
          active = JSON.parse(decodeURIComponent(cookieValue));
        } else {
          const stored = sessionStorage.getItem("hire3x_heera_notifications");
          if (stored) active = JSON.parse(stored);
        }
      } catch (e) {
        console.error(e);
      }
      if (!active.includes(action)) {
        active.push(action);
        try {
          sessionStorage.setItem("hire3x_heera_notifications", JSON.stringify(active));
          document.cookie = `hire3x_heera_notifications=${encodeURIComponent(JSON.stringify(active))}; ${COOKIE_OPTIONS_SUFFIX}`;
        } catch (e) {
          console.error(e);
        }
        EventBus.$emit("heera-visited-updated");
      }
    },
  },
};
</script>

<style scoped>
.heera-card-container {
  position: fixed;
  top: 70px;
  right: 24px;
  width: 365px;
  height: auto;
  background: #ffffff;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 0.5px solid rgba(196, 220, 246, 1);
  border-radius: 16px;
  box-sizing: border-box;
  padding: 24px 20px 20px 20px;
  box-shadow: 0px 8px 32px rgba(74, 126, 255, 0.08);
  z-index: 1040;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  transition: box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.heera-card-container:hover {
  box-shadow: 0px 12px 40px rgba(74, 126, 255, 0.14);
}

/* Close button on the top right */
.close-card-btn {
  position: absolute;
  top: 16px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.close-card-btn:hover .close-icon-img {
  transform: scale(1.15) rotate(90deg);
  opacity: 0.85;
}

.close-icon-img {
  display: block;
  position: relative;
  top: -7px;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

/* Flex content layout */
.heera-card-content {
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  height: 100%;
}

.avatar-column {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.heera-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: contain;
  display: block;
}

.text-column {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
  text-align: left;
}

.heera-title {
  margin: 0 0 6px 0;
  line-height: 1.2;
}

.heera-desc {
  margin: 15px 0 15px 0 !important;
  line-height: 1.4;
  color: #515151;
  text-align: left;
}

/* CTA Scan button style */
.heera-cta-btn {
  background-color: #4A7EFF !important;
  border: none;
  border-radius: 15.87px;
  height: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
}

.heera-cta-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0px 4px 12px rgba(32, 99, 155, 0.25);
}

.heera-cta-btn:active {
  transform: translateY(0);
}

/* Premium Entering & Leaving animations */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.fade-slide-enter {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

/* Premium dynamic text transition animation */
.fade-content-enter-active,
.fade-content-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-content-enter {
  opacity: 0;
  transform: translateY(5px);
}

.fade-content-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Responsiveness: hide Heera notification modal on mobile and tablet viewports */
@media (max-width: 1028px) {
  .heera-card-container {
    display: none !important;
  }
}
</style>
